import { Box, Container, Stack, Typography } from '@mui/material';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import type { ReactNode } from 'react';

interface PainPoint {
  icon: ReactNode;
  title: string;
  body: string;
}

const items: ReadonlyArray<PainPoint> = [
  {
    icon: <VisibilityOffRoundedIcon />,
    title: 'Unclear vision',
    body: 'You can feel something is off, but the next-best move is hard to name.',
  },
  {
    icon: <LockRoundedIcon />,
    title: 'Fear of change',
    body: 'The cost of standing still is starting to outweigh the discomfort of moving.',
  },
  {
    icon: <HelpOutlineRoundedIcon />,
    title: 'Lack of self awareness',
    body: 'Patterns keep repeating; the blind spot is hard to see from the inside.',
  },
  {
    icon: <ScheduleRoundedIcon />,
    title: 'Unreliable accountability',
    body: 'Goals get set, then quietly slide. There is no one structurally on your side.',
  },
  {
    icon: <RecordVoiceOverRoundedIcon />,
    title: 'Poor communication',
    body: 'The message in your head is not the message your team is receiving.',
  },
  {
    icon: <SpeedRoundedIcon />,
    title: 'Improper capacities',
    body: 'You’re leading from yesterday’s level when today demands more of you.',
  },
];

export function PainPoints() {
  return (
    <Box
      component="section"
      id="pain-points"
      sx={{ backgroundColor: '#ebe5d6', py: { xs: 10, md: 14 } }}
    >
      <Container maxWidth="lg">
        <Stack
          spacing={2}
          sx={{ textAlign: 'center', alignItems: 'center', mb: { xs: 6, md: 8 } }}
        >
          <Typography variant="overline" sx={{ color: 'primary.main' }}>
            Where leaders get stuck
          </Typography>
          <Typography
            variant="h2"
            component="h2"
            sx={{ color: 'primary.dark', fontSize: { xs: '2rem', md: '2.6rem' }, maxWidth: 720 }}
          >
            Sound familiar?
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
            gap: { xs: 3, md: 4 },
          }}
        >
          {items.map((item) => (
            <Stack
              key={item.title}
              spacing={1.75}
              sx={{
                alignItems: 'flex-start',
                p: 3.5,
                backgroundColor: 'background.paper',
                borderRadius: 1.5,
                border: '1px solid rgba(31, 61, 46, 0.08)',
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1,
                  backgroundColor: 'rgba(31, 61, 46, 0.08)',
                  color: 'primary.main',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </Box>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  color: 'primary.dark',
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 600,
                  fontSize: '1.05rem',
                }}
              >
                {item.title}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {item.body}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
